import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Utilitários globais para formatação e manipulação de dados
 */

/**
 * Converte uma data ISO ou string para formato brasileiro (dd/mm/aaaa)
 * Evita problemas de timezone que causam diferença de 1 dia
 * @param dateString - Data em formato ISO (yyyy-mm-dd) ou string de data
 * @returns Data formatada em pt-BR ou string vazia se inválida
 */
export function formatDateToBR(dateString: string): string {
  if (!dateString) return '';
  
  // Verifica se é formato ISO (yyyy-mm-dd) e faz conversão direta
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }
  
  // Fallback para outras formatações usando Date
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? dateString : date.toLocaleDateString('pt-BR');
}

/**
 * Converte uma Data para formato YYYY-MM-DD para inputs de data
 * @param date - Objeto Date
 * @returns Data em formato ISO (yyyy-mm-dd)
 */
export function dateToYMD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Obtém o mês atual no formato YYYY-MM
 * @returns Mês atual em formato YYYY-MM
 */
export function getCurrentYearMonth(): string {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

/**
 * Formata um valor monetário.
 * O locale textual é sempre 'pt-BR' (separadores, vírgula decimal).
 * @param value - Valor numérico
 * @param currency - Código ISO 4217 (padrão 'BRL'). Use `useFormatters()` para obter
 *                   automaticamente a moeda escolhida pelo usuário.
 * @returns Valor formatado, ex: "R$ 1.234,50" ou "US$ 1.234,50"
 */
export function formatCurrency(value: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(value);
}

/**
 * Converte string de data para formato que pode ser usado em input[type="date"]
 * @param dateString - Data em qualquer formato
 * @returns Data no formato yyyy-mm-dd ou string vazia
 */
export function dateStringToInputValue(dateString: string): string {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  return dateToYMD(date);
}
